import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axiosConfig';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    CircularProgress,
    Alert
} from '@mui/material';

const AcceptInvitePage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [invitationDetails, setInvitationDetails] = useState(null);

    useEffect(() => {
        const validateInvitation = async () => {
            try {
                const response = await api.get(`/api/groups/invitations/validate/${token}`);
                if (response.data.success) {
                    setInvitationDetails(response.data.invitation);
                    // Pre-fill email if available
                    setFormData(prev => ({
                        ...prev,
                        email: response.data.invitation.email
                    }));
                } else {
                    setError('This invitation link is invalid or has expired');
                }
            } catch (error) {
                setError('Failed to validate invitation');
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            validateInvitation();
        }
    }, [token]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const response = await api.post('/api/groups/accept-invitation', {
                token,
                ...formData
            });

            if (response.data.success) {
                setSuccess(true);
                // Store the token temporarily
                localStorage.setItem('token', response.data.token);
                
                // Redirect to dashboard after a brief delay
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to accept invitation');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                ) : (
                    <>
                        <Typography variant="h5" component="h1" gutterBottom>
                            Accept Team Invitation
                        </Typography>

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Account created successfully! Redirecting...
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Confirm Password"
                                name="confirmPassword"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                sx={{ mt: 3 }}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Create Account'}
                            </Button>
                        </form>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default AcceptInvitePage; 